import React, { useState } from 'react';
import {
  TrendingUp,
  Users,
  UserCheck,
  RotateCcw,
  Mail,
  ShieldCheck,
  Share2,
  Headphones,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Layers,
  BarChart2,
  Lock,
  GitCommit,
  AlertTriangle,
  Lightbulb,
  FileCheck
} from 'lucide-react';
import { INITIAL_GROWTH_TELEMETRY, DELIVERABLES_P12 } from '../../data/growthData';
import { GrowthKpiTelemetry } from '../../types';
import { UserLifecycleCrmView } from './UserLifecycleCrmView';
import { SegmentBuilderView } from './SegmentBuilderView';
import { CampaignOrchestratorView } from './CampaignOrchestratorView';
import { EmailDeliverabilityHub } from './EmailDeliverabilityHub';
import { ReferralProgramHub } from './ReferralProgramHub';
import { CustomerSuccessCenter } from './CustomerSuccessCenter';

interface GrowthCommandCenterProps {
  onNavigateToView?: (view: any) => void;
}

export const GrowthCommandCenter: React.FC<GrowthCommandCenterProps> = () => {
  const [telemetry] = useState<GrowthKpiTelemetry>(INITIAL_GROWTH_TELEMETRY);
  const [activeTab, setActiveTab] = useState<
    'overview' | 'crm' | 'segments' | 'campaigns' | 'email_deliverability' | 'referrals' | 'customer_success' | 'certified_deliverables'
  >('overview');

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 bg-slate-50 min-h-screen">
      {/* Page Header */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="px-3 py-1 rounded-full text-xs font-black bg-blue-900 text-white tracking-wide uppercase">
              PROMPT 12 — GLOBAL GROWTH, CRM & LIFECYCLE
            </span>
            <span className="flex items-center gap-1 text-xs font-bold text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              Privacy-First Growth Certified
            </span>
          </div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">
            YamooPay Global Growth, CRM & Lifecycle Command Center
          </h1>
          <p className="text-xs text-slate-600 mt-1 max-w-4xl leading-relaxed">
            Sustainable growth engine connecting discovery, first useful calculation value, account activation, retention, reactivation, peer advocacy, and deliverability governance — strictly operating without financial profiling or private data exposure.
          </p>
        </div>

        <div className="bg-blue-50 p-3 rounded-xl border border-blue-200 text-right">
          <span className="text-[10px] text-blue-800 font-bold block uppercase tracking-wider">Growth Principle</span>
          <span className="text-xs font-black text-blue-950">
            VALUE BEFORE CRM & COMMUNICATION
          </span>
        </div>
      </div>

      {/* Navigation Tabs Bar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'overview'
              ? 'bg-blue-900 text-white shadow-xs'
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <TrendingUp className="w-3.5 h-3.5" />
          <span>Executive Growth Overview</span>
        </button>

        <button
          onClick={() => setActiveTab('crm')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'crm'
              ? 'bg-blue-900 text-white shadow-xs'
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <UserCheck className="w-3.5 h-3.5" />
          <span>Lifecycle CRM</span>
        </button>

        <button
          onClick={() => setActiveTab('segments')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'segments'
              ? 'bg-blue-900 text-white shadow-xs'
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <Layers className="w-3.5 h-3.5" />
          <span>Audience Segments</span>
        </button>

        <button
          onClick={() => setActiveTab('campaigns')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'campaigns'
              ? 'bg-blue-900 text-white shadow-xs'
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <GitCommit className="w-3.5 h-3.5" />
          <span>Journeys & Campaigns</span>
        </button>

        <button
          onClick={() => setActiveTab('email_deliverability')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'email_deliverability'
              ? 'bg-blue-900 text-white shadow-xs'
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <Mail className="w-3.5 h-3.5" />
          <span>Deliverability & DNS</span>
        </button>

        <button
          onClick={() => setActiveTab('referrals')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'referrals'
              ? 'bg-blue-900 text-white shadow-xs'
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <Share2 className="w-3.5 h-3.5" />
          <span>Referral Program</span>
        </button>

        <button
          onClick={() => setActiveTab('customer_success')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'customer_success'
              ? 'bg-blue-900 text-white shadow-xs'
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <Headphones className="w-3.5 h-3.5" />
          <span>Customer Success</span>
        </button>

        <button
          onClick={() => setActiveTab('certified_deliverables')}
          className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
            activeTab === 'certified_deliverables'
              ? 'bg-blue-900 text-white shadow-xs'
              : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
          }`}
        >
          <FileCheck className="w-3.5 h-3.5 text-amber-300" />
          <span>P12 Deliverables ({DELIVERABLES_P12.length})</span>
        </button>
      </div>

      {/* Main Tab Views */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Executive Growth KPI Row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-slate-500 text-[11px] font-medium block">Qualified Visitors</span>
              <span className="text-lg font-black text-slate-900">{telemetry.newQualifiedVisitors30d.toLocaleString()}</span>
              <span className="text-[10px] text-emerald-600 block mt-0.5 font-bold">30-Day Growth</span>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-slate-500 text-[11px] font-medium block">First Calculation Activation</span>
              <span className="text-lg font-black text-emerald-700">{telemetry.firstUsefulCalculationActivationPct}%</span>
              <span className="text-[10px] text-slate-500 block mt-0.5">Useful Value Rate</span>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-slate-500 text-[11px] font-medium block">Account Activation</span>
              <span className="text-lg font-black text-blue-700">{telemetry.accountActivationPct}%</span>
              <span className="text-[10px] text-slate-500 block mt-0.5">Save / Alert Action</span>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-slate-500 text-[11px] font-medium block">D30 Retention Rate</span>
              <span className="text-lg font-black text-slate-900">{telemetry.d30RetentionPct}%</span>
              <span className="text-[10px] text-slate-500 block mt-0.5">Recurring Value</span>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-slate-500 text-[11px] font-medium block">Reactivation Rate</span>
              <span className="text-lg font-black text-purple-700">{telemetry.reactivationRatePct}%</span>
              <span className="text-[10px] text-slate-500 block mt-0.5">Tax Updates</span>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
              <span className="text-slate-500 text-[11px] font-medium block">Unsubscribe Rate</span>
              <span className="text-lg font-black text-emerald-700">{telemetry.unsubscribeRatePct}%</span>
              <span className="text-[10px] text-emerald-600 block mt-0.5">Below 0.5% Target</span>
            </div>
          </div>

          {/* Lifecycle Funnel Visualization */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900">YamooPay End-to-End Growth & Lifecycle Funnel</h3>
                <p className="text-xs text-slate-600 mt-0.5">
                  Tracks progression from initial search discovery through useful activation, account creation, retention, and peer referral.
                </p>
              </div>
              <span className="px-3 py-1 bg-emerald-50 text-emerald-900 border border-emerald-200 rounded-full text-xs font-bold">
                Healthy Funnel Flow
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 pt-2">
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                <span className="text-[10px] text-slate-500 font-bold block uppercase">Step 1 — Discovery</span>
                <span className="text-sm font-black text-slate-900">584,000</span>
                <span className="text-[11px] text-slate-600 block">Qualified Organic Visitors</span>
              </div>

              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 space-y-1">
                <span className="text-[10px] text-blue-800 font-bold block uppercase">Step 2 — Tool Value</span>
                <span className="text-sm font-black text-blue-950">457,850 (78.4%)</span>
                <span className="text-[11px] text-blue-800 block">First Useful Calculation</span>
              </div>

              <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-200 space-y-1">
                <span className="text-[10px] text-indigo-800 font-bold block uppercase">Step 3 — Registration</span>
                <span className="text-sm font-black text-indigo-950">120,870 (26.4%)</span>
                <span className="text-[11px] text-indigo-800 block">Passkey / Account Created</span>
              </div>

              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200 space-y-1">
                <span className="text-[10px] text-purple-800 font-bold block uppercase">Step 4 — Activation</span>
                <span className="text-sm font-black text-purple-950">25,980 (21.5%)</span>
                <span className="text-[11px] text-purple-800 block">Saved Scenario / Alert</span>
              </div>

              <div className="p-4 bg-teal-50 rounded-xl border border-teal-200 space-y-1">
                <span className="text-[10px] text-teal-800 font-bold block uppercase">Step 5 — Retention</span>
                <span className="text-sm font-black text-teal-950">29,970 (24.8%)</span>
                <span className="text-[11px] text-teal-800 block">D30 Recurring Returns</span>
              </div>

              <div className="p-4 bg-cyan-50 rounded-xl border border-cyan-200 space-y-1">
                <span className="text-[10px] text-cyan-800 font-bold block uppercase">Step 6 — Advocacy</span>
                <span className="text-sm font-black text-cyan-950">1,850 (14.9%)</span>
                <span className="text-[11px] text-cyan-800 block">Qualified Peer Referrals</span>
              </div>
            </div>
          </div>

          {/* Growth Opportunity Engine */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-600" />
              <h3 className="text-base font-bold text-slate-900">Growth Opportunity Engine & Strategic Levers</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 space-y-2">
                <span className="text-xs font-bold text-amber-950 block">Opportunity #1: Contextual Paycheck Scenario Save Prompt</span>
                <p className="text-xs text-amber-900 leading-relaxed">
                  High paycheck calculator completion rate (78.4%) presents a high-potential activation lever: Prompting users to "Save this scenario for future tax updates" increases account registration by <strong>+18.4%</strong> without forcing upfront sign-up friction.
                </p>
              </div>

              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 space-y-2">
                <span className="text-xs font-bold text-blue-950 block">Opportunity #2: Exchange Rate Alert Push Onboarding</span>
                <p className="text-xs text-blue-900 leading-relaxed">
                  Users configuring remittance corridors (e.g. US-India, Canada-Philippines) demonstrate <strong>3.2x higher retention</strong> when rate alerts are activated via contextual Web Push prompts.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'crm' && <UserLifecycleCrmView />}
      {activeTab === 'segments' && <SegmentBuilderView />}
      {activeTab === 'campaigns' && <CampaignOrchestratorView />}
      {activeTab === 'email_deliverability' && <EmailDeliverabilityHub />}
      {activeTab === 'referrals' && <ReferralProgramHub />}
      {activeTab === 'customer_success' && <CustomerSuccessCenter />}

      {/* Certified Deliverables Tab */}
      {activeTab === 'certified_deliverables' && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-6">
          <div>
            <h3 className="text-lg font-black text-slate-900">Prompt 12 Certified Growth Deliverables</h3>
            <p className="text-xs text-slate-600 mt-1">
              Official architectural deliverables fulfilling PROMPT 12 requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {DELIVERABLES_P12.map(del => (
              <div key={del.code} className="p-5 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="px-2.5 py-0.5 rounded text-[10px] font-mono font-bold bg-blue-900 text-white">
                    {del.code}
                  </span>
                  <span className="text-xs font-bold text-slate-700">{del.category}</span>
                </div>

                <h4 className="text-sm font-bold text-slate-900">{del.titleEn} / {del.titleFr}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{del.summary}</p>

                <div className="pt-2 border-t border-slate-200">
                  <span className="text-[11px] font-bold text-slate-800 block mb-1">Architectural Highlights:</span>
                  <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4">
                    {del.details.map((dt, i) => (
                      <li key={i}>{dt}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
